import { useEffect, useMemo } from "react";

import { PalButton, PalTextField } from "@palamar/fe-library";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { Grid } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRContainer from "~components/Generic/PRContainer";
import PRPage from "~components/Generic/PRPage";
import HistoryHelper from "~helpers/HistoryHelper";
import { selectCurrentBot, selectCurrentProject } from "~store/user/selectors";
import {
  createOrUpdateUserUnderstanding,
  getUserUnderstanding,
  setUserUnderstanding,
} from "~store/userUnderstanding/actions";
import { selectUserUnderstanding } from "~store/userUnderstanding/selectors";

const UserMoodEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const dispatch = useDispatch();

  const currentProject = useSelector(selectCurrentProject);
  const currentBot = useSelector(selectCurrentBot); // Assuming you have a selector for the current bot
  const userMood = useSelector(selectUserUnderstanding); // Assuming you have a selector for user mood

  const handleClickCancel = () => {
    HistoryHelper.goBack("/chatbot/user-understanding/mood/", { scope: "dashboard" });
  };

  useEffect(() => {
    if (!id) return;
    dispatch(getUserUnderstanding(currentProject.id, currentBot.id, id));

    return () => {
      dispatch(setUserUnderstanding(null)); // Clear user mood when component unmounts
    };
    // Page should be redirected to list if selected project is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, currentProject.id, currentBot.id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: id
      ? {
          id: userMood?.id,
          name: userMood?.name || "",
          description: userMood?.description || "",
          emoji: userMood?.emoji || "",
        }
      : {
          name: "",
          description: "",
          emoji: "",
        },
    validationSchema: Yup.object({
      name: Yup.string().required(t("component.formik.required").format(t("common.name"))),
      description: Yup.string().required(t("component.formik.required").format(t("common.description"))),
      emoji: Yup.string().required(t("component.formik.required").format(t("dashboard.userMood.emoji"))),
    }),
    onSubmit: async (values) => {
      await dispatch(
        createOrUpdateUserUnderstanding(currentProject.id, currentBot.id, {
          ...values,
          class_type: 0,
        })
      );
      //   await dispatch(getCurrentUser());
      handleClickCancel();
    },
  });

  useProjectChange(() => {
    HistoryHelper.push("/chatbot/user-understanding/mood/", { scope: "dashboard" });
  }, []);

  const parentName = useMemo(
    () => [
      {
        label: t("dashboard.userMood"),
        url: "/chatbot/user-understanding/mood/",
      },
      {
        label: id ? t("dashboard.userMood.edit") : t("dashboard.userMood.create"),
      },
    ],
    [id, t]
  );

  return (
    <PRContainer description={t("dashboard.userMood.description")} name={t("common.chatbot")} parentName={parentName}>
      <PRPage title={id ? t("dashboard.userMood.edit") : t("dashboard.userMood.create")}>
        <Grid container spacing={2}>
          <Grid item md={9} xs={6}>
            <PalTextField
              fullWidth
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              label={t("common.name")}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={3} xs={6}>
            <PalTextField
              fullWidth
              error={formik.touched.emoji && Boolean(formik.errors.emoji)}
              helperText={formik.touched.emoji && formik.errors.emoji}
              label={t("dashboard.userMood.emoji")}
              name="emoji"
              value={formik.values.emoji}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <PalTextField
              fullWidth
              multiline
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              label={t("common.description")}
              name="description"
              rows={4}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
          </Grid>

          {/* Handle Submit */}
          <Grid item className="d-flex justify-content-end" xs={12}>
            <PalButton
              color="primary"
              disabled={formik.isSubmitting || !formik.isValid}
              loading={formik.isSubmitting}
              onClick={formik.handleSubmit}
            >
              {t("common.save")}
            </PalButton>
            <PalButton color="secondary" disabled={formik.isSubmitting} onClick={handleClickCancel}>
              {t("common.cancel")}
            </PalButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default UserMoodEdit;
